// Simulator registry.
//
// Maps a stable kind id → React component for every Tier 2 panel
// authored in this repo. New equipment plugs in via one entry: write
// the panel under `./<kind>.tsx`, register it here, and any consumer
// can render it by id without touching its imports.
//
// Keep ids in `kebab-case` and stable — they appear in module
// metadata, scenario configs, and curriculum content.

import type { ComponentType } from "react";
import CrossoverSoloPanel from "./crossover-solo-panel";
import PTZAWRP50 from "./ptz-aw-rp50";
import TelexBP1002 from "./telex-bp-1002";

export type SimulatorKind =
  | "crossover-solo"
  | "ptz-aw-rp50"
  | "telex-bp-1002";

interface SimulatorEntry {
  kind: SimulatorKind;
  /** Friendly label for menus, debug panels, and the sandbox page. */
  label: string;
  /** Manufacturer + model — used for the panel banner ("PRACTICING ON: …"). */
  hardware: string;
  /** Equipment crew this simulator serves. */
  crew: "audio" | "video" | "stage" | "it";
  /**
   * Whether the simulator reads the active program-moment scenario from
   * `<ScenarioProvider>` (for scene master + per-cam preset libraries).
   * Surfaces mounting this simulator outside a provider rely on its
   * built-in default-scenario fallback.
   */
  supportsScenarios?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
}

export const SIMULATORS: Readonly<Record<SimulatorKind, SimulatorEntry>> = {
  "crossover-solo": {
    kind: "crossover-solo",
    label: "Ross Crossover Solo",
    hardware: "Ross Crossover Solo · 1 MLE production switcher",
    crew: "video",
    supportsScenarios: true,
    Component: CrossoverSoloPanel,
  },
  "ptz-aw-rp50": {
    kind: "ptz-aw-rp50",
    label: "Panasonic AW-RP50 PTZ Controller",
    hardware: "Panasonic AW-RP50",
    crew: "video",
    supportsScenarios: true,
    Component: PTZAWRP50,
  },
  "telex-bp-1002": {
    kind: "telex-bp-1002",
    label: "Telex BP-1002 Beltpack",
    hardware: "Telex BP-1002 · single-channel intercom beltpack",
    crew: "stage",
    Component: TelexBP1002,
  },
} as const;

export const SIMULATOR_KINDS = Object.keys(SIMULATORS) as SimulatorKind[];
